import { z } from "zod";

export const AgentProvider = z.enum(["claude", "codex", "gemini", "kimi"]);
export type AgentProvider = z.infer<typeof AgentProvider>;

/** Provider presentation + model presets. The server's provider registry holds the CLI mechanics. */
/** Providers with a separate fast-output option (mirrors ProviderDef.supportsFast on the server). */
export const PROVIDER_SUPPORTS_FAST: Record<AgentProvider, boolean> = { claude: true, codex: true, gemini: false, kimi: true };
export const PROVIDERS: Record<AgentProvider, { label: string; vendor: string; icon: string; color: string; models: { id: string; label: string }[]; beta: boolean; loginHint: string }> = {
  claude: { label: "Claude Code", vendor: "Anthropic", icon: "✴️", color: "#d97757", beta: false, loginHint: "claude auth login", models: [{ id: "default", label: "Default" }, { id: "sonnet", label: "Sonnet" }, { id: "opus", label: "Opus" }, { id: "haiku", label: "Haiku" }] },
  codex: { label: "Codex", vendor: "OpenAI", icon: "🟢", color: "#10a37f", beta: true, loginHint: "codex login", models: [{ id: "default", label: "Default" }, { id: "gpt-5-codex", label: "GPT-5 Codex" }, { id: "gpt-5", label: "GPT-5" }, { id: "o4-mini", label: "o4-mini" }] },
  // Google moved personal accounts (free, AI Pro, AI Ultra) from Gemini CLI to Antigravity CLI (agy) on 2026-06-18:
  // the "gemini" provider runs agy, signed in with the Google account (its subscription applies)
  gemini: { label: "Antigravity", vendor: "Google", icon: "✨", color: "#4285f4", beta: true, loginHint: "agy → Google OAuth", models: [{ id: "default", label: "Default" }] },
  kimi: { label: "Kimi Code", vendor: "Moonshot", icon: "🌙", color: "#6366f1", beta: true, loginHint: "kimi → /login", models: [{ id: "default", label: "Default" }, { id: "kimi-k2-thinking", label: "K2 Thinking" }, { id: "kimi-k2", label: "K2" }] },
};

export const AccountStatus = z.enum(["pending", "connected", "disconnected", "error"]);
export type AccountStatus = z.infer<typeof AccountStatus>;

export const AccountInfo = z.object({
  loggedIn: z.boolean(),
  authMethod: z.string().nullable(),
  email: z.string().nullable(),
  subscriptionType: z.string().nullable(),
  organization: z.string().nullable(),
});
export type AccountInfo = z.infer<typeof AccountInfo>;

/** A provider login that lives in its own credentials volume and auth container.
 *  Projects reference an account; the credentials volume is mounted into their containers. */
export const AgentAccount = z.object({
  id: z.string(),
  provider: AgentProvider,
  label: z.string(),
  ownerId: z.string(),
  ownerName: z.string(),
  /** Shared accounts (admin-created) are selectable by every project owner. */
  shared: z.boolean(),
  status: AccountStatus,
  info: AccountInfo.nullable(),
  checkedAt: z.string().nullable(),
  projectsUsing: z.number(),
  createdAt: z.string(),
});
export type AgentAccount = z.infer<typeof AgentAccount>;

/** Guided login: the CLI runs in a hidden PTY; the UI shows the link and a box for the code. */
export const LoginPhase = z.enum(["starting", "waiting_code", "finishing", "done", "failed"]);
export const LoginMode = z.enum(["code", "device"]);
export type LoginMode = z.infer<typeof LoginMode>;
export const LoginState = z.object({
  phase: LoginPhase,
  /** "code": the site shows a code the user pastes here. "device": we show a code the user types on the site. */
  mode: LoginMode.default("code"),
  /** Device flow: the one-time code to enter on the provider's page. */
  userCode: z.string().nullable().default(null),
  /** The OAuth link printed by the CLI (open it in a new tab). */
  url: z.string().nullable(),
  /** Cleaned tail of what the CLI printed, for troubleshooting. */
  output: z.string(),
  exitCode: z.number().nullable(),
  /** Last thing the CLI said after a code was submitted (e.g. "Invalid code"). */
  message: z.string().nullable().default(null),
  startedAt: z.string(),
});
export type LoginState = z.infer<typeof LoginState>;
export const SubmitLoginCodeInput = z.object({ code: z.string().trim().min(4).max(400) });

export const CreateAccountInput = z.object({
  provider: AgentProvider.default("claude"),
  label: z.string().trim().min(1).max(60),
  shared: z.boolean().default(false),
});
export type CreateAccountInput = z.infer<typeof CreateAccountInput>;
export const SetAccountSharedInput = z.object({ shared: z.boolean() });

// ---------------- chats ----------------

export const ChatStatus = z.enum(["idle", "running", "error"]);
export type ChatStatus = z.infer<typeof ChatStatus>;

/** Model id as the provider CLI expects it ("default" = the provider's own default). Presets live in PROVIDERS. */
export const ClaudeModel = z.string().trim().min(1).max(80).regex(/^[a-zA-Z0-9._:-]+$/);
export type ClaudeModel = z.infer<typeof ClaudeModel>;

/** Per-chat run settings, like the original's settings sheet. */
export const ThinkingLevel = z.enum(["auto", "off", "high", "ultra"]);
export const ChatMode = z.enum(["code", "chat", "plan", "review", "debug", "auto"]);
/** Output speed, separate from the model (Claude Code's fast mode, Codex's priority tier, Kimi's highspeed variant). */
export const SpeedLevel = z.enum(["standard", "fast"]);
export type SpeedLevel = z.infer<typeof SpeedLevel>;
export const ChatSettings = z.object({ thinking: ThinkingLevel.default("auto"), mode: ChatMode.default("code"), speed: SpeedLevel.default("standard") });
export type ChatSettings = z.infer<typeof ChatSettings>;
/** Work folder relative to /workspace ("" = the workspace root). */
export const WorkFolder = z.string().trim().max(200).regex(/^(?!.*\.\.)[^\0]*$/, "no .. segments").transform((v) => v.replace(/^\/+|\/+$/g, ""));

export const Chat = z.object({
  id: z.string(),
  projectId: z.string(),
  title: z.string(),
  model: ClaudeModel,
  providerSessionId: z.string().nullable(),
  useMemory: z.boolean(),
  settings: ChatSettings,
  cwd: z.string(),
  /** Account (provider) this chat runs with; null = automatic (project override, else first connected). */
  agentAccountId: z.string().nullable(),
  /** Studio agent this chat is pinned to: messages without a mention go to it. Null = the chat's own provider/model. */
  pinnedAgentHandle: z.string().nullable(),
  /** Provider session per provider, so switching providers mid-chat and back keeps each context. */
  sessions: z.record(z.string(), z.string()),
  /** True until the user renames the chat; auto titles come from the first finished run. */
  titleAuto: z.boolean(),
  pinned: z.boolean(),
  archived: z.boolean(),
  unread: z.boolean(),
  status: ChatStatus,
  lastSeq: z.number(),
  /** "multi": one question asked to several providers at once; its members are hidden child chats. */
  kind: z.enum(["single", "multi"]),
  /** Child chats of a multi chat: the parent's id, and "member" (one provider), "combine" (merges the answers) or "memory" (the hidden memory keeper). */
  parentId: z.string().nullable(),
  role: z.enum(["member", "combine", "memory", "summary", "organizer"]).nullable(),
  /** Chat section: the space this conversation belongs to */
  spaceId: z.string().nullable(),
  /** Chat section: the conversation whose memory this one shares (null: its own memory) */
  memoryFrom: z.string().nullable(),
  /** Chat section: a temporary conversation, deleted with all its files once the user leaves it (never listed) */
  temporary: z.boolean(),
  /** Continued from a conversation that grew too long, with a hand-over summary (null while it is being written). */
  continuedFrom: z.string().nullable(),
  carryover: z.string().nullable(),
  /** The newest conversation this one was continued in. */
  continuedIn: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Chat = z.infer<typeof Chat>;
/** From this many events a conversation is "long": the page suggests continuing it in a new one. */
export const LONG_CHAT_EVENTS = 3000;

export const CreateChatInput = z.object({ title: z.string().trim().max(120).default(""), model: ClaudeModel.default("default"), cwd: WorkFolder.default("") });
export const UpdateChatInput = z.object({ title: z.string().trim().min(1).max(120).optional(), model: ClaudeModel.optional(), useMemory: z.boolean().optional(), settings: ChatSettings.partial().optional(), cwd: WorkFolder.optional(), agentAccountId: z.string().nullable().optional(), pinnedAgentHandle: z.string().regex(/^[a-z][a-z0-9_]{2,31}$/).nullable().optional(), pinned: z.boolean().optional(), archived: z.boolean().optional(), unread: z.boolean().optional(), spaceId: z.string().nullable().optional(), memoryFrom: z.string().nullable().optional() });
/** One question to several providers at once: one member chat per account, each with its own session and folder. */
export const CreateMultiChatInput = z.object({ accountIds: z.array(z.string()).min(1).max(8), title: z.string().trim().max(120).default(""), model: ClaudeModel.optional(), spaceId: z.string().nullable().optional(), temporary: z.boolean().optional() });
export type CreateMultiChatInput = z.infer<typeof CreateMultiChatInput>;
export const CombineInput = z.object({ accountId: z.string() });
export const AddChatMemberInput = z.object({ accountId: z.string() });
/** A multi chat with its member chats (and the combine chat once answers were merged). The parent's
 *  agentAccountId is the pinned provider: plain messages go to that member only (null = every member). */
export interface MultiChat {
  chat: Chat;
  members: Chat[];
  combine: Chat | null;
}
/** What a send to a multi chat did for each member. */
export interface MultiSendResult {
  chatId: string;
  runId: string | null;
  error: string | null;
}

/** A file uploaded into the project workspace for the agent to read. */
export const ChatAttachment = z.object({
  name: z.string().min(1).max(200),
  /** Absolute path inside the container, under /workspace/.codvero/attachments/<chatId>/ */
  path: z.string().min(1).max(500),
  size: z.number().int().min(0),
  mime: z.string().max(100),
});
export type ChatAttachment = z.infer<typeof ChatAttachment>;
// Chat attachments have no size limit (owner's decision, 2026-10-08).

export const SendPromptInput = z.object({
  text: z.string().min(1).max(100_000),
  attachments: z.array(ChatAttachment).max(20).default([]),
  /** Multi chat: send only to these member chats (default: every member). */
  only: z.array(z.string()).max(20).optional(),
});
export type SendPromptInput = z.infer<typeof SendPromptInput>;

/** Sequential task list: prompts queued on a chat and sent one after another as runs finish. */
export const QueueItemStatus = z.enum(["queued", "running", "done", "failed"]);
export const ChatQueueItem = z.object({
  id: z.string(),
  chatId: z.string(),
  text: z.string(),
  attachments: z.array(ChatAttachment),
  position: z.number(),
  status: QueueItemStatus,
  createdAt: z.string(),
});
export type ChatQueueItem = z.infer<typeof ChatQueueItem>;
/** Rewind drops the user prompt at `seq` and everything after it (provider session included). */
export const RewindInput = z.object({ seq: z.number().int().min(1) });
/** Fork copies the chat (and provider session) up to, but not including, the user prompt at `seq`; omit for the whole chat. */
export const ForkInput = z.object({ seq: z.number().int().min(1).optional() });
export const ReorderQueueInput = z.object({ ids: z.array(z.string()).max(200) });

/** One persisted line of a chat. `payload` is the raw provider event for provider events. */
export const ChatEventType = z.enum(["user_prompt", "provider", "run_started", "run_finished", "run_error", "run_cancelled", "task", "files_saved"]);
/** Files a tool brought back (an MCP server's pictures, videos, archives), kept in the workspace by the platform. */
export interface SavedFile {
  /** absolute, in the container (/workspace/…) */
  path: string;
  name: string;
  size: number;
  mime: string;
}
export interface FilesSavedPayload {
  tool: string;
  /** where they were kept: the space's files, or the conversation's folder */
  dir: string;
  inSpace: boolean;
  files: SavedFile[];
}
export type ChatEventType = z.infer<typeof ChatEventType>;
export const ChatEvent = z.object({
  seq: z.number(),
  runId: z.string().nullable(),
  type: ChatEventType,
  payload: z.unknown(),
  at: z.string(),
});
export type ChatEvent = z.infer<typeof ChatEvent>;
/** A page of older events: [from, the seq asked for). `from` 0 = nothing older is left. */
export interface ChatEventsPage { from: number; events: ChatEvent[] }

/** WebSocket frames on /ws/chat/:id */
export const ChatWsServerMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("event"), event: ChatEvent }),
  /** The replay on open, in batches (one frame per batch instead of one per event). */
  z.object({ type: z.literal("events"), events: z.array(ChatEvent) }),
  /** Sent before a tail replay: only events with seq >= from follow; older ones load on demand (0 = the whole chat). */
  z.object({ type: z.literal("window"), from: z.number() }),
  z.object({ type: z.literal("chat"), chat: Chat }),
  z.object({ type: z.literal("queue"), items: z.array(ChatQueueItem) }),
  /** After a rewind: drop every event with seq > lastSeq. */
  z.object({ type: z.literal("truncate"), lastSeq: z.number() }),
  z.object({ type: z.literal("error"), code: z.string(), message: z.string().optional() }),
]);
export type ChatWsServerMessage = z.infer<typeof ChatWsServerMessage>;

/** Host-wide agent policy (admin): how many runs at once, and which language agents reply in. */
export const AgentsSettings = z.object({ maxConcurrentRuns: z.number().int().min(1).max(50).default(5), replyLanguage: z.enum(["auto", "ar", "en"]).default("auto") });
export type AgentsSettings = z.infer<typeof AgentsSettings>;

/** Who is speaking in a run: a studio agent (name + avatar) or the provider itself. Payload of `run_started`. */
export const RunStartedPayload = z.object({
  runId: z.string(),
  model: z.string(),
  provider: AgentProvider,
  account: z.object({ id: z.string(), label: z.string() }).nullable(),
  agent: z.object({ handle: z.string(), name: z.string(), avatar: z.object({ emoji: z.string(), color: z.string(), image: z.string().nullable().optional() }) }).nullable(),
});
export type RunStartedPayload = z.infer<typeof RunStartedPayload>;

// ---------------- MCP connections (Chat section) ----------------

/** Providers whose CLI can take extra MCP servers (Kimi Code has no MCP configuration). */
export const MCP_PROVIDERS: AgentProvider[] = ["claude", "codex", "gemini"];
export const McpName = z.string().regex(/^[a-z][a-z0-9_-]{1,31}$/, "lowercase letters, digits, - and _ (2–32, starting with a letter)").refine((v) => v !== "browser", "\"browser\" is the platform's own browser tool");
/** An MCP server the user's Chat section runs can use: a command run inside the space, or a remote HTTP server. */
export const McpServer = z.object({
  id: z.string(),
  name: z.string(),
  transport: z.enum(["stdio", "http"]),
  command: z.string().nullable(),
  args: z.array(z.string()),
  url: z.string().nullable(),
  /** names only: the values are stored encrypted and never sent back */
  envKeys: z.array(z.string()),
  headerKeys: z.array(z.string()),
  /** null = every provider that supports MCP */
  providers: z.array(AgentProvider).nullable(),
  /** remote servers that ask for an account (OAuth): "required" until the user connects, then "connected" */
  oauth: z.enum(["none", "required", "connected"]),
  enabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type McpServer = z.infer<typeof McpServer>;
const SecretValue = z.string().max(8000).nullable();
/** Header names may hold "-" (X-Api-Key); environment variable names cannot (bash refuses `export A-B=…`). */
const HeaderMap = z.record(z.string().regex(/^[A-Za-z_][A-Za-z0-9_-]{0,63}$/), SecretValue);
const EnvMap = z.record(z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, "letters, digits and _ only"), SecretValue);
/** env / headers: a value sets it, null keeps the stored value, a missing key removes it. */
export const UpsertMcpServerInput = z
  .object({
    name: McpName,
    transport: z.enum(["stdio", "http"]),
    command: z.string().trim().max(500).optional(),
    args: z.array(z.string().max(2000)).max(50).default([]),
    url: z.string().trim().url().max(2000).optional(),
    env: EnvMap.default({}),
    headers: HeaderMap.default({}),
    providers: z.array(AgentProvider).min(1).nullable().default(null),
    enabled: z.boolean().default(true),
  })
  .refine((v) => (v.transport === "stdio" ? !!v.command : !!v.url), { message: "a command (local) or a URL (remote) is required" });
export type UpsertMcpServerInput = z.infer<typeof UpsertMcpServerInput>;

// ---------------- Chat section spaces ----------------

/** Like a Claude project: instructions every provider gets in its conversations, plus reference files. */
/** The icons a space can wear (lucide names; the web app maps them). */
export const SPACE_ICONS = ["folder", "briefcase", "house", "megaphone", "camera", "palette", "code", "book", "rocket", "heart", "star", "shopping-bag", "utensils", "music", "graduation-cap", "building", "chart", "globe", "sparkles", "coffee"] as const;
export const SPACE_COLORS = ["#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#ef4444", "#f97316", "#eab308", "#14b8a6", "#64748b", "#a16207"] as const;

export const ChatSpace = z.object({
  id: z.string(),
  name: z.string(),
  instructions: z.string(),
  color: z.string().nullable(),
  icon: z.string().nullable(),
  /** a small image (data: URL) shown instead of the icon */
  logo: z.string().nullable(),
  chatCount: z.number(),
  /** where its reference files live in the personal space */
  filesDir: z.string(),
  /** kept at the top of the Chat section's sidebar */
  pinned: z.boolean(),
  /** taken off the sidebar (its conversations are listed by date there) */
  hidden: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ChatSpace = z.infer<typeof ChatSpace>;
export const UpsertChatSpaceInput = z.object({
  name: z.string().trim().min(1).max(80),
  instructions: z.string().max(50_000).default(""),
  // left out: unchanged; null: back to the default
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  icon: z.enum(SPACE_ICONS).nullable().optional(),
  /** the browser resizes the picture first, so this stays a small data: URL (no SVG: it could carry script) */
  logo: z.string().regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/).max(400_000).nullable().optional(),
});
export type UpsertChatSpaceInput = z.infer<typeof UpsertChatSpaceInput>;
/** A file a provider made in one of a space's conversations (listed once, where it was last written). */
export const SpaceOutput = z.object({
  path: z.string(),
  name: z.string(),
  size: z.number(),
  modifiedAt: z.string(),
  /** the conversation to open (a multi chat's parent) and the one that made it (its member, or the same) */
  openChatId: z.string(),
  chatId: z.string(),
  chatTitle: z.string(),
  /** the prompt it answered, in `chatId` (where the conversation scrolls to) */
  turnSeq: z.number().nullable(),
  provider: AgentProvider.nullable(),
  /** a file of this name is among the space's own files */
  inMyFiles: z.boolean(),
});
export type SpaceOutput = z.infer<typeof SpaceOutput>;
/** A saved prompt; {{name}} marks a blank asked for when it is used. */
export const PromptTemplate = z.object({ id: z.string(), spaceId: z.string().nullable(), title: z.string(), body: z.string(), createdAt: z.string(), updatedAt: z.string() });
export type PromptTemplate = z.infer<typeof PromptTemplate>;
export const UpsertPromptTemplateInput = z.object({ title: z.string().trim().min(1).max(80), body: z.string().trim().min(1).max(20_000), spaceId: z.string().nullable().default(null) });
export type UpsertPromptTemplateInput = z.infer<typeof UpsertPromptTemplateInput>;
/** The blanks of a template, in order, each once. */
export const templateBlanks = (body: string): string[] => [...new Set([...body.matchAll(/\{\{\s*([^{}]{1,40}?)\s*\}\}/g)].map((m) => m[1]!))];
export const fillTemplate = (body: string, values: Record<string, string>): string => body.replace(/\{\{\s*([^{}]{1,40}?)\s*\}\}/g, (_m, k: string) => values[k] ?? "");

/** A provider usage window: the share used (0..1) and when it resets (ms since epoch). */
export interface LimitWindow { used: number; resetsAt: number | null }
export interface AccountUsage {
  id: string;
  provider: string;
  label: string;
  ownerName: string;
  shared: boolean;
  /** what the provider last reported (null: it reports nothing, e.g. Antigravity) */
  limits: { fiveHour?: LimitWindow | null; weekly?: LimitWindow | null; limited: boolean; at: string } | null;
  renewsAt: string | null;
  runs5h: number;
  runs7d: number;
  tokens7d: number;
  cost7d: number;
}
export interface UsageReport {
  accounts: AccountUsage[];
  /** runs per day (last 14 days), per provider */
  daily: { day: string; provider: string; runs: number }[];
  users: { id: string; name: string; runs7d: number; runs30d: number }[];
  failures: { runId: string; chatId: string; chatTitle: string; projectId: string; personal: boolean; provider: string | null; at: string; message: string | null }[];
  /** renewals in the coming 7 days */
  renewals: { accountId: string; label: string; provider: string; renewsAt: string; days: number }[];
}
export const SetAccountRenewalInput = z.object({ renewsAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((d) => !Number.isNaN(Date.parse(d)) && new Date(d).toISOString().startsWith(d), "not a date").nullable() });

export interface SpaceFile {
  name: string;
  size: number;
  modifiedAt: string;
}

/** A shortcut an MCP server offers (an MCP prompt), shown above the message box after "/server". */
export interface McpPrompt {
  name: string;
  title?: string | undefined;
  description?: string | undefined;
  arguments: { name: string; description?: string | undefined; required: boolean }[];
}
export const GetMcpPromptInput = z.object({ name: z.string().min(1).max(200), arguments: z.record(z.string(), z.string().max(20_000)).default({}) });

/** Who keeps a Chat section conversation's memory after each turn (Settings → My memory). */
export const MemoryKeeperInput = z.object({ accountId: z.string().min(1), model: z.string().trim().min(1).max(80) });
export type MemoryKeeperInput = z.infer<typeof MemoryKeeperInput>;
export interface MemoryKeeper {
  /** null: no connected account to keep memory with */
  accountId: string | null;
  model: string;
  /** true when the user chose it; false: the default (a Claude account, Haiku) */
  chosen: boolean;
}

